import { AppNode, ContinuousNode, MixtureNode, NodeProperties } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getUpdatedNodeProperties } from "@features/builder/NodeDrawer/NodeForm/helpers"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import {
  createNodeLabelMap,
  createNodeTypeMap,
  isNodeDfCpdRowUsingParentAsDistributionParameter,
  rowsToDfCpd,
} from "@utils/node"
import { Grid } from "@mui/material"
import { useNodes } from "@xyflow/react"
import { useEffect, useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { continuousNodeDistributionProperties } from "utils/graph"
import ContinuousNodeTable from "@features/builder/NodeDrawer/NodeForm/ContinuousNodeTable"
import { useAtom } from "jotai"
import { selectedPropertiesRowAtom } from "@features/builder/NodeDrawer/NodeForm/propertiesTableAtom"
import DistributionNotAvailable from "./DistributionNotAvailable"
import { DistributionChart } from "@components/Distribution/DistributionChart"
import { useDfCpdPropertiesValidation } from "@features/builder/NodeDrawer/NodeForm/useDfCpdPropertiesValidation"
import { hasChildNodes } from "@utils/node/getParentsList"

type ContinuousPropertiesTableAccordionProps = {
  // mixture is basically continuous, but with categorical parent, so we can just share table component
  node: ContinuousNode | MixtureNode
  onPropertiesUpdate: (properties: NodeProperties) => void
}

const ContinuousPropertiesTableAccordion = (props: ContinuousPropertiesTableAccordionProps) => {
  const nodeProperties = props.node.data.originalNode.properties

  const allNodes = useNodes<AppNode>()
  const labelMap = useMemo(() => createNodeLabelMap(allNodes), [allNodes])
  const nodeTypeMap = useMemo(() => createNodeTypeMap(allNodes), [allNodes])
  const form = useFormContext<NodeFormValues>()
  const [selectedRowIndex, setSelectedRowIndex] = useAtom(selectedPropertiesRowAtom)

  const propertiesFormValues = useWatch({ control: form.control, name: "properties" })
  const dfCpd = useMemo(() => rowsToDfCpd(propertiesFormValues), [propertiesFormValues])

  useEffect(() => {
    setSelectedRowIndex(0)
  }, [props.node.id, setSelectedRowIndex])

  const handleDistributionChange = (newDistribution: string, rowIndex: number) => {
    const formPropertiesValues = form.getValues("properties") ?? []
    if (!formPropertiesValues[rowIndex]) return
    formPropertiesValues[rowIndex].distribution = newDistribution

    const updatedProperties = getUpdatedNodeProperties(
      nodeProperties,
      formPropertiesValues,
      continuousNodeDistributionProperties,
    )
    props.onPropertiesUpdate(updatedProperties)
  }

  const parentIds = nodeProperties.parents_lst
  const selectedRow = propertiesFormValues[selectedRowIndex] ?? {}
  const isUsingParent = isNodeDfCpdRowUsingParentAsDistributionParameter(selectedRow, parentIds)
  const hasChildren = useMemo(
    () => hasChildNodes(props.node.id, allNodes),
    [allNodes, props.node.id],
  )
  const dfcpdErrors = useDfCpdPropertiesValidation({ dfCpd, hasChildren })

  return (
    <NodeDrawerAccordion id="properties-table" label="Properties table">
      <Grid item>
        <ContinuousNodeTable
          nodeLabelMap={labelMap}
          nodeTypeMap={nodeTypeMap}
          onDistributionChange={handleDistributionChange}
          type={props.node.type}
          nodeProperties={nodeProperties}
          enableSelection
          validationErrors={dfcpdErrors}
        />
        {isUsingParent ? (
          <DistributionNotAvailable />
        ) : (
          <DistributionChart rowIndex={selectedRowIndex} dfCpd={dfCpd} />
        )}
      </Grid>
    </NodeDrawerAccordion>
  )
}

export default ContinuousPropertiesTableAccordion
